import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { TaskStatus } from '@types'

import ToDoIcon from '@assets/images/task-status-to-do-icon.svg?react'
import DoneIcon from '@assets/images/task-status-done-icon.svg?react'
import WarningIcon from '@assets/images/task-status-warning-icon.svg?react'
import InProgressIcon from '@assets/images/task-status-in-progress-icon.svg?react'

import { Select, Option } from '@components/Select'

import { ItemContainer, ItemLabel, StatusContainer } from './SelectTaskStatus.styled'

interface Props {
  required?: boolean
  status?: TaskStatus
  onSelectTaskStatus: (status?: TaskStatus) => void
  label?: string
  dropdownAutoWidth?: boolean
  placeholder?: string
  disabled?: boolean
}

export const SelectTaskStatus = (props: Props): JSX.Element => {
  const {
    required,
    status,
    onSelectTaskStatus,
    label,
    placeholder,
    dropdownAutoWidth = false,
    disabled = false,
  } = props

  const { t } = useTranslation()

  const options: Option[] = [
    {
      id: TaskStatus.StatusToDo,
      label: t('enum_TaskStatus_to_do_label'),
      data: TaskStatus.StatusToDo,
      Component: () => <TaskStatusComponent status={TaskStatus.StatusToDo} />,
    },
    {
      id: TaskStatus.StatusWarning,
      label: t('enum_TaskStatus_warning_label'),
      data: TaskStatus.StatusWarning,
      Component: () => <TaskStatusComponent status={TaskStatus.StatusWarning} />,
    },
    {
      id: TaskStatus.StatusInProgress,
      label: t('enum_TaskStatus_in_progress_label'),
      data: TaskStatus.StatusInProgress,
      Component: () => <TaskStatusComponent status={TaskStatus.StatusInProgress} />,
    },
    {
      id: TaskStatus.StatusDone,
      label: t('enum_TaskStatus_done_label'),
      data: TaskStatus.StatusDone,
      Component: () => <TaskStatusComponent status={TaskStatus.StatusDone} />,
    },
  ]

  const selectedOption: Option | undefined = useMemo(() => {
    return options.find((option) => option.id == status)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  return (
    <Select
      required={required}
      label={label}
      dropdownAutoWidth={dropdownAutoWidth}
      placeholder={placeholder}
      selected={selectedOption}
      options={options}
      disabled={disabled}
      onClick={(option) => {
        if (option) {
          onSelectTaskStatus(option.data as TaskStatus)
        } else {
          onSelectTaskStatus()
        }
      }}
    />
  )
}

interface TaskStatusComponentProps {
  status: TaskStatus
}

const TaskStatusComponent = (props: TaskStatusComponentProps): JSX.Element => {
  const { status } = props

  const { t } = useTranslation()

  return (
    <ItemContainer>
      <StatusContainer status={status}>
        {status == TaskStatus.StatusToDo && <ToDoIcon width={18} height={18} />}
        {status == TaskStatus.StatusInProgress && <InProgressIcon />}
        {status == TaskStatus.StatusDone && <DoneIcon />}
        {status == TaskStatus.StatusWarning && <WarningIcon />}
      </StatusContainer>

      <ItemLabel>
        {status == TaskStatus.StatusToDo && t('enum_TaskStatus_to_do_label')}
        {status == TaskStatus.StatusInProgress && t('enum_TaskStatus_in_progress_label')}
        {status == TaskStatus.StatusDone && t('enum_TaskStatus_done_label')}
        {status == TaskStatus.StatusWarning && t('enum_TaskStatus_warning_label')}
      </ItemLabel>
    </ItemContainer>
  )
}
